import { useEffect, useRef, useState, useCallback } from 'react';
import { getBaseUrl, getAuthHeaders, ActivityEvent } from '../utils/api';

interface UseEventStreamOptions {
  enabled: boolean;
  onEvent: (event: ActivityEvent, isLiveUpdate: boolean) => void;
  pollInterval?: number;
}

export function useEventStream({
  enabled,
  onEvent,
  pollInterval = 5000,
}: UseEventStreamOptions) {
  const [connected, setConnected] = useState(false);
  const lastEventIdRef = useRef<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);
  const seenEventIdsRef = useRef<Set<string>>(new Set());

  const poll = useCallback(async () => {
    if (!enabled) return;

    try {
      const since =
        lastEventIdRef.current ||
        new Date(Date.now() - 60000).toISOString();

      const url = `${getBaseUrl()}/api/activity?since=${encodeURIComponent(since)}&limit=20`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setConnected(true);
        const data = await response.json();
        const events = data.events as ActivityEvent[];

        // Process new events (skip duplicates)
        for (const event of events) {
          if (!seenEventIdsRef.current.has(event.id)) {
            seenEventIdsRef.current.add(event.id);
            // Pass false for initial load (no toasts), true for live updates
            onEvent(event, !isInitialLoadRef.current);
          }
          lastEventIdRef.current = event.created_at;
        }

        // After first poll, mark initial load as complete
        isInitialLoadRef.current = false;
      } else {
        setConnected(false);
      }
    } catch (err) {
      console.error('Polling error:', err);
      setConnected(false);
    }
  }, [enabled, onEvent]);

  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setConnected(false);
      return;
    }

    // Initial poll
    poll();

    // Set up polling interval
    intervalRef.current = window.setInterval(poll, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, poll, pollInterval]);

  return { connected };
}
